import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { employeeRoute, jobApplicationPageRoute } from '../web-routes';

import { LinkReferralButton } from './link-referral-button';
import { RemoveReferralButton } from './remove-referral-button';

import * as Status from '../config/application-status-config';
import { formatPhoneNumber, formatRelativeDate, formatDate } from '../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

type InputProps = {
  orgId: number;
  referrals: Referral[];
  isLinked: boolean;
};

const DEFAULT_PAGE_SIZE = 5;

export const EmployeeReferralTable = (props: InputProps) => {
  const pageSize = DEFAULT_PAGE_SIZE;
  const { referrals, orgId, isLinked } = props;
  const [offset, setOffset] = useState<number>(0);

  const changePage = (_event: any, page: number) => {
    setOffset(page * pageSize);
  };

  return referrals.length === 0 ? (
    <div className="u-textAlign-center typo-h6 u-padding-2x">No referrals yet</div>
  ) : (
    <div className="u-padding-bottom-lg">
      <Table>
        <TableHead>
          <TableRow>
            {/* If the table is for linked referrals, we want to show all
                    relevant information which includes contact info (ex. so the
                    hiring lead may reach out to the applicant if they have not
                    yet applied) and application status. If however the table
                    is for unlinked referrals we are handling the case where an
                    applicant has already applied and listed a referring
                    employee on their application. Thus we do not need to show
                    contact info or status, but do need to include a row for a
                    button which lets the user strictly attribute the referral
                    to an employee. */}
            {isLinked ? (
              <>
                <TableCell className="u-width-20">Applicant Name</TableCell>
                <TableCell className="u-width-15">Job</TableCell>
                <TableCell className="u-width-30">Contact</TableCell>
                <TableCell className="u-width-20">Referred By</TableCell>
                <TableCell className="u-width-15">Status</TableCell>
              </>
            ) : (
              <>
                <TableCell className="u-width-20">Applicant Name</TableCell>
                <TableCell className="u-width-15">Job</TableCell>
                <TableCell className="u-width-30"> </TableCell>
                <TableCell className="u-width-20">Referred By</TableCell>
                <TableCell className="u-width-15">Link Or Remove Referral</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {referrals.slice(offset, offset + pageSize).map((row) => {
            const { Person } = row.Contact;
            const employee = row.Employee?.Contact.Person;
            return (
              <TableRow key={row.referralId}>
                <TableCell component="th">
                  {row.JobApplicant ? (
                    <Link
                      className="typo-link"
                      to={jobApplicationPageRoute(row.JobApplicant.job_applicant_id)}
                      target="_blank"
                    >
                      {Person.firstName} {Person.lastName}
                    </Link>
                  ) : (
                    <span>
                      {Person.firstName} {Person.lastName}
                    </span>
                  )}
                </TableCell>
                <TableCell component="th">{row.Job ? row.Job.title : 'Other'}</TableCell>
                {isLinked ? (
                  <TableCell component="th">
                    <div>Email: {Person.email ? Person.email : '--'}</div>
                    <div>
                      Phone: {Person.phoneNumber ? formatPhoneNumber(Person.phoneNumber) : '--'}
                    </div>
                  </TableCell>
                ) : (
                  <TableCell component="th"> </TableCell>
                )}
                <TableCell component="th">
                  {isLinked && row.Employee && employee ? (
                    <Link
                      className="typo-link"
                      to={employeeRoute(orgId, row.Employee.employeeId)}
                      target="_blank"
                    >
                      {employee.firstName} {employee.lastName}
                    </Link>
                  ) : (
                    <div>{row.referrerName}</div>
                  )}
                  <div className="typo-subtitle">On {formatDate(row.datetime)}</div>
                </TableCell>
                {isLinked && (
                  <TableCell component="th">
                    {row.JobApplicant ? (
                      <>
                        <Link
                          className="typo-link"
                          to={jobApplicationPageRoute(row.JobApplicant.job_applicant_id)}
                          target="_blank"
                        >
                          {Status.statusDisplay(row.JobApplicant.status)}{' '}
                          <MaterialDesignIcon
                            iconName={ICON_NAMES.OPEN_IN_NEW}
                            size="sm"
                            className="u-verticalAlign-middle u-padding-left-md"
                          />
                        </Link>
                        {row.JobApplicant.hireStartDateTime ? (
                          <div className="typo-subtitle">
                            {formatRelativeDate(row.JobApplicant.hireStartDateTime)}, on{' '}
                            {formatDate(row.JobApplicant.hireStartDateTime)}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      'No Application'
                    )}
                  </TableCell>
                )}
                {/* If table is unlinked, let last column be link / remove referral buttons */}
                {!isLinked && (
                  <TableCell component="th">
                    <div className="u-flex-center u-gap-10px">
                      <LinkReferralButton orgId={orgId} referral={row} />
                      <RemoveReferralButton referral={row} />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {referrals.length >= pageSize && (
        <TablePagination
          component="div"
          count={referrals.length}
          rowsPerPage={pageSize}
          page={offset / pageSize}
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onPageChange={changePage}
          rowsPerPageOptions={[pageSize]}
        />
      )}
    </div>
  );
};
